import { useState, useEffect, useCallback } from 'react';
import { useMedplum } from '@medplum/react';
import { Practitioner } from '@medplum/fhirtypes';
import { DiagnosticReportReviewStatus } from '../../lims-types';
import {
  checkAllReviewersApproved,
  findReviewerTask,
} from '../../utils/fhir-helpers';

// -----------------------------------------------------------------------------
interface ReviewStatusData {
  allApproved: boolean;
  totalTasks: number;
  approvedTasks: number;
  pendingTasks: number;
  approvedReviewerIds: string[];
  pendingReviewerIds: string[];
}

// -----------------------------------------------------------------------------
export const useReviewStatus = (
  reportId?: string,
  currentUser?: Practitioner
) => {
  const medplum = useMedplum();
  const [reviewStatusData, setReviewStatusData] =
    useState<ReviewStatusData | null>(null);
  const [currentUserReviewStatus, setCurrentUserReviewStatus] =
    useState<DiagnosticReportReviewStatus | null>(null);

  // ---------------------------------------------------------------------------
  const loadReviewStatus = useCallback(async () => {
    if (!reportId || !currentUser?.id) {
      return;
    }

    try {
      const reviewStatus = await checkAllReviewersApproved(medplum, reportId);
      setReviewStatusData(reviewStatus);

      // Check current user's status by looking at their review task
      if (reviewStatus.approvedReviewerIds.includes(currentUser.id)) {
        setCurrentUserReviewStatus('approved');
      } else if (reviewStatus.pendingReviewerIds.includes(currentUser.id)) {
        // Find the user's specific task to check their latest review decision
        const userTask = await findReviewerTask(
          medplum,
          reportId,
          currentUser.id
        );

        if (userTask) {
          // Check task outputs for the latest review decision
          const reviewDecisionOutput = userTask.output?.find(
            (output) => output.type?.coding?.[0]?.code === 'review-decision'
          );

          const latestReviewDecision = reviewDecisionOutput?.valueString;

          if (latestReviewDecision === 'changes-requested') {
            setCurrentUserReviewStatus('changes-requested');
          } else {
            setCurrentUserReviewStatus('pending');
          }
        } else {
          setCurrentUserReviewStatus('pending');
        }
      } else {
        setCurrentUserReviewStatus(null);
      }
    } catch (error) {
      console.error('Error loading review status:', error);
    }
  }, [reportId, medplum, currentUser?.id]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    loadReviewStatus();
  }, [loadReviewStatus]);

  // ---------------------------------------------------------------------------
  return {
    reviewStatusData,
    currentUserReviewStatus,
    setCurrentUserReviewStatus,
    refetch: loadReviewStatus,
  };
};
